import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import PasswordInput from '../components/PasswordInput';
import { Label } from '../components/ui/label';
import { TrendingUp, Mail, Lock, User, Loader2, Gift, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const Register = () => {
  const { register } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    promo_code: '',
  });
  const [showPromoField, setShowPromoField] = useState(false);
  const [promoStatus, setPromoStatus] = useState(null); // { valid, message } | null
  const [checkingPromo, setCheckingPromo] = useState(false);

  const checkPromo = async () => {
    const code = formData.promo_code.trim();
    if (!code) { setPromoStatus(null); return; }
    setCheckingPromo(true);
    try {
      const { data } = await axios.get(`${API}/promo/validate/${encodeURIComponent(code)}`);
      setPromoStatus(data);
    } catch (_e) {
      setPromoStatus({ valid: false, reason: 'Erreur de validation' });
    } finally {
      setCheckingPromo(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const promoToSend = promoStatus?.valid ? formData.promo_code.trim().toUpperCase() : null;
      // Read founder intent BEFORE calling register (AuthContext consumes the flag on success)
      let founderIntent = false;
      try { founderIntent = localStorage.getItem('tradex_founder_intent') === '1'; } catch (_) { /* ignore */ }

      await register(formData.email, formData.password, formData.name, promoToSend);

      if (founderIntent) {
        toast.success('🏛 Bienvenue Fondateur ! Votre place vient d\'être réservée — accès offert 3 mois.');
      } else if (promoToSend) {
        toast.success(`🎁 Code ${promoToSend} activé — profitez de votre essai Expert !`);
      } else {
        toast.success(t('common.success'));
      }
      navigate('/dashboard');
    } catch (error) {
      console.error('Register error:', error);
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md space-y-8 animate-fade-in">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 mb-4">
            <TrendingUp className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-3xl font-bold">{t('auth.registerTitle')}</h1>
          <p className="text-muted-foreground">{t('auth.registerSubtitle')}</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="glass-card p-8 space-y-6" data-testid="register-form">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">{t('auth.name')}</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input id="name" type="text" data-testid="name-input" placeholder="John Doe"
                  className="pl-10 bg-secondary/50 border-white/10 focus:border-primary/50"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">{t('auth.email')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input id="email" type="email" data-testid="email-input" placeholder="you@example.com"
                  className="pl-10 bg-secondary/50 border-white/10 focus:border-primary/50"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">{t('auth.password')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground z-10" />
                <PasswordInput id="password" data-testid="password-input" placeholder="••••••••"
                  className="pl-10 bg-secondary/50 border-white/10 focus:border-primary/50"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  autoComplete="new-password" required minLength={8} />
              </div>
            </div>

            {/* Promo code — collapsible */}
            {!showPromoField ? (
              <button
                type="button"
                onClick={() => setShowPromoField(true)}
                className="w-full flex items-center justify-center gap-2 py-2 text-sm text-primary hover:text-primary/80 hover:underline transition-colors"
                data-testid="toggle-promo-field"
              >
                <Gift className="w-4 h-4" />
                J&apos;ai un code promo ou d&apos;affiliation
              </button>
            ) : (
              <div className="space-y-2 pt-2 border-t border-white/5">
                <Label htmlFor="promo_code" className="flex items-center gap-2">
                  <Gift className="w-4 h-4 text-emerald-400" />
                  Code promo (optionnel)
                </Label>
                <div className="relative">
                  <Input
                    id="promo_code"
                    type="text"
                    data-testid="promo-code-input"
                    placeholder="Ex: LAUNCH7"
                    className="bg-secondary/50 border-white/10 focus:border-primary/50 uppercase font-mono pr-10"
                    value={formData.promo_code}
                    onChange={(e) => {
                      setFormData({ ...formData, promo_code: e.target.value.toUpperCase() });
                      setPromoStatus(null);
                    }}
                    onBlur={checkPromo}
                    maxLength={30}
                  />
                  {checkingPromo && (
                    <Loader2 className="w-4 h-4 animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  )}
                  {!checkingPromo && promoStatus?.valid && (
                    <CheckCircle2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-emerald-400" />
                  )}
                  {!checkingPromo && promoStatus && !promoStatus.valid && (
                    <XCircle className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-red-400" />
                  )}
                </div>
                {promoStatus?.valid && (
                  <div className="text-xs text-emerald-400 flex items-center gap-1.5 p-2 rounded bg-emerald-500/5 border border-emerald-500/20" data-testid="promo-valid-message">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    {promoStatus.message}
                  </div>
                )}
                {promoStatus && !promoStatus.valid && (
                  <div className="text-xs text-red-400 flex items-center gap-1.5" data-testid="promo-invalid-message">
                    <XCircle className="w-3.5 h-3.5" />
                    {promoStatus.reason}
                  </div>
                )}
              </div>
            )}
          </div>

          <Button
            type="submit"
            data-testid="submit-register"
            className="w-full bg-primary text-primary-foreground hover:bg-primary/90 py-6 font-mono uppercase tracking-wider"
            disabled={loading}
          >
            {loading ? (<><Loader2 className="w-5 h-5 mr-2 animate-spin" />{t('common.loading')}</>) : t('auth.registerBtn')}
          </Button>

          {/* Divider */}
          <div className="relative py-2">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-card px-3 text-xs text-muted-foreground uppercase tracking-widest">ou</span>
            </div>
          </div>

          {/* Google Sign-Up — REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
          <Button
            type="button"
            variant="outline"
            data-testid="google-signup-btn"
            className="w-full bg-white text-gray-900 hover:bg-gray-100 border-white/20 py-6"
            onClick={() => {
              // Stash referral/promo so AuthCallback can attach them to the new account
              try {
                if (formData.referred_by_code) localStorage.setItem('tradex_ref_code', formData.referred_by_code);
                if (formData.promo_code) localStorage.setItem('tradex_pending_promo', formData.promo_code);
              } catch (_e) { /* ignore */ }
              const redirectUrl = window.location.origin + '/dashboard';
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
          >
            <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continuer avec Google
          </Button>
        </form>

        <p className="text-center text-muted-foreground">
          {t('auth.hasAccount')}{' '}
          <Link to="/login" className="text-primary hover:underline" data-testid="login-link">
            {t('nav.login')}
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Register;
